import React, { useEffect, useMemo, useState } from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import MDBox from "components/MDBox";
import { buildCostParts } from "layouts/ecommerce/products/new-product/crm/invoice/finance/shared/costParts";
import MDTypography from "components/MDTypography";
import Footer from "../../../../../examples/Footer";
import DashboardNavbar from "../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../examples/LayoutContainers/DashboardLayout";
import MDButton from "../../../../../components/MDButton";
import { useNavigate, useLocation } from "react-router-dom";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { appAlert } from "components/AppDialog";

import { CrmRowActions, ListIconButton, ListLink, CrmPageHeader, CrmActionButton, CrmNotice, CrmTableCard } from "../crm/shared/CrmUi";
const normalizeApiList = (res) => {
    const raw = res?.data;

    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.data?.data)) return raw.data.data;

    return [];
};

const renderText = (value, fallback = "-") => {
    if (value === null || value === undefined || value === "") return fallback;

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    if (Array.isArray(value)) {
        const text = value.map((item) => renderText(item, "")).filter(Boolean).join(", ");
        return text || fallback;
    }

    if (typeof value === "object") {
        return (
            value.name ||
            value.label ||
            value.companyName ||
            value.title ||
            value.value ||
            value.id ||
            value._id ||
            fallback
        );
    }

    return fallback;
};

const formatDateTime = (d, t) => {
    if (!d && !t) return "-";
    if (!d && t) return renderText(t);

    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return renderText(t);

    const date = dt.toLocaleDateString("tr-TR");
    return `${date}${t ? ` ${renderText(t, "")}` : ""}`;
};

const formatMoney = (v, currency = "") => {
    const n = Number(v || 0);
    return `${n.toFixed(2)}${currency ? ` ${renderText(currency, "")}` : ""}`;
};

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const firstPositiveNumber = (...values) => {
    for (const value of values) {
        const number = toNumber(value);
        if (number > 0) return number;
    }

    return 0;
};

const getInvoiceItemsTotalCost = (items = []) => {
    if (!Array.isArray(items)) return 0;

    return items.reduce(
        (sum, item) =>
            sum +
            firstPositiveNumber(
                item?.totalCost,
                item?.cost,
                item?.unitCost,
                item?.buyingRate
            ),
        0
    );
};

const getPricingTotalCost = (pricing) => {
    if (!pricing || typeof pricing !== "object") return 0;

    const items = Array.isArray(pricing) ? pricing : Object.values(pricing);
    return items.reduce(
        (sum, item) =>
            sum +
            firstPositiveNumber(
                item?.totalCost,
                item?.cost,
                item?.unitCost,
                item?.buyingRate
            ),
        0
    );
};

const cardSx = {
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
};

const infoCardSx = {
    p: 2,
    minWidth: 180,
    border: "1px solid #eef2f7",
    borderRadius: "12px",
    background: "#f9fbfd",
};

const getBlTypeLabel = (attr = {}) => {
    const shipments = getMblShipments(attr);
    const firstShipment = shipments?.[0];
    const firstAttr = firstShipment?.attributes || firstShipment || {};

    if (
        attr.billOfLadingType === "DIRECT_BL" ||
        attr.isDirectBL === true ||
        attr.sourceType === "DBL" ||
        firstAttr.billOfLadingType === "DIRECT_BL" ||
        firstAttr.isDirectBL === true ||
        firstAttr.sourceType === "DBL"
    ) {
        return "DBL";
    }

    return "AMBL";
};

const isDirectBlRecord = (attr = {}) => getBlTypeLabel(attr) === "DBL";

const getMblShipments = (attr = {}) => {
    const shipments = attr.shipments || attr.shipmentDetails || [];
    return Array.isArray(shipments) ? shipments : [];
};

const getFirstShipmentIdFromMbl = (attr = {}) => {
    const shipments = getMblShipments(attr);
    const firstShipment = shipments?.[0];

    if (!firstShipment) return "";

    const row = firstShipment?.attributes || firstShipment || {};

    return firstShipment?.id || row?.id || row?._id || row?.shipmentId || "";
};

const AirConsolidation = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [mbls, setMbls] = useState([]);
    const [notification, setNotification] = useState({
        value: false,
        text: "",
        type: "error",
    });
    const [selectedMbl, setSelectedMbl] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, mbl: null });

    const fetchMbls = async () => {
        try {
            const res = await CrudService.getAMBLs();
            setMbls(normalizeApiList(res));
        } catch (e) {
            console.error("getMBLs error:", e);
            setMbls([]);
        }
    };

    useEffect(() => {
        fetchMbls();
    }, []);

    useEffect(() => {
        const prefill = location.state;

        if (!prefill?.fromOmbl) return;

        const run = async () => {
            try {
                if (prefill?.shipments?.length) {
                    const t = prefill.totals || {};

                    const consolidationData = {
                        amblNumber: prefill.ambl?.amblNumber,
                        mbl: prefill.ambl,
                        shipments: prefill.shipments.map((s) => s.attributes || s),
                        totals: {
                            totalLdm: Number(t.ldm || 0),
                            totalCbm: Number(t.cbm || 0),
                            totalVolume: Number(t.volume || 0),
                            totalChargeableWeight: Number(t.chargeableWeight || 0),
                            totalGrossWeight: Number(t.grossWeight || 0),
                            plannedRouteCountries: prefill.ambl?.plannedRouteCountries || [],
                        },
                    };

                    await CrudService.createConsolidation({ data: consolidationData });
                }

                await fetchMbls();

                navigate(location.pathname, {
                    replace: true,
                    state: {},
                });
            } catch (err) {
                console.error("fromMbl flow error:", err?.response?.data || err);

                setNotification({
                    value: true,
                    text: "AMBL/DBL flow failed. Please try again.",
                    type: "error",
                });
            }
        };

        run();
    }, [location.state, navigate, location.pathname]);

    const clickDeleteMbl = async () => {
        try {
            const id = deleteDialog?.mbl?.id;

            if (!id) return;

            await CrudService.deleteAMBL(id);
            await fetchMbls();

            if (selectedMbl?.id === id) {
                setSelectedMbl(null);
            }

            setDeleteDialog({ open: false, mbl: null });

            setNotification({
                value: true,
                text: "AMBL/DBL deleted successfully",
                type: "success",
            });
        } catch (e) {
            console.error("deleteMBL error:", e);

            setDeleteDialog({ open: false, mbl: null });

            setNotification({
                value: true,
                text: "AMBL/DBL delete failed",
                type: "error",
            });
        }
    };

    const handleEditMbl = (mbl) => {
        if (!mbl) return;

        const attr = mbl?.attributes || {};

        if (isDirectBlRecord(attr)) {
            const shipmentId = getFirstShipmentIdFromMbl(attr);

            if (shipmentId) {
                navigate(
                    `/shipping-order/create-air-shipment/details?id=${shipmentId}&openDmbl=1&dmblId=${mbl.id}`
                );
                return;
            }
        }

        navigate(`/air-shipping-order/connect-ambl?id=${mbl.id}`);
    };

    const handleGoShipmentForm = (shipmentId) => {
        if (!shipmentId) return;

        navigate(`/shipping-order/create-air-shipment/details?id=${shipmentId}`);
    };

    const buildInvoicePayload = (mblAttr) => {
        const shipments = getMblShipments(mblAttr);

        if (!Array.isArray(shipments) || shipments.length === 0) return null;

        const formattedShipments = shipments.map((s, idx) => {
            const row = s?.attributes || s;
            const invoiceItems = Array.isArray(row.invoiceItems)
                ? row.invoiceItems
                : [];
            const rowFinancialSummary = row.financialSummary || {};
            const invoiceItemsTotalCost = getInvoiceItemsTotalCost(invoiceItems);
            const shipmentPricingTotalCost = getPricingTotalCost(row.shipmentPricing);

            const totalCost = firstPositiveNumber(
                row.totalCost,
                row.actualCostTotal,
                row.expectedCostTotal,
                row.operationalCost,
                // Shipment'in kendi maliyet alanları (CreateAirShipment.js'de kullanıcı
                // tarafından girilir); önceden bu liste dışındaydı ve tek shipment'lı
                // olmayan AMBL'lerde totalCost hep 0'a düşüyordu.
                row.buyingRate,
                row.pickupRate,
                row.deliveryRate,
                row.exportCustomsRate,
                row.importCustomsRate,
                invoiceItemsTotalCost,
                shipmentPricingTotalCost,
                rowFinancialSummary.totalCost,
                rowFinancialSummary.buyingRate,
            );
            const costCurrency =
                row.buyingRateCurrency || rowFinancialSummary.totalCurrency || mblAttr?.financialSummary?.totalCurrency || "";

            return {
                shipmentNo: row.shipmentNo,
                sourceType: "air",
                customer: renderText(row.customer, ""),
                sellingRateCurrency: row.sellingRateCurrency,
                sellingRate: row.sellingRate,
                finalSellingRate: row.finalSellingRate,
                buyingRateCurrency: row.buyingRateCurrency,
                costCurrency,
                totalCostCurrency: costCurrency,
                profit: row.profit || 0,
                totals: row.totals || {},
                carbonFootprint: row.carbonFootprint || {},
                invoiceItems,
                cargoDetails: row.cargoDetails || [],
                shipmentPricing: row.shipmentPricing || {},
                pricingDetails: row.pricingDetails || [],
                costItems: row.costItems || [],
                extraCosts: row.extraCosts || [],
                financialSummary: rowFinancialSummary,
                freightCharges: row.freightCharges || {},
                totalCost,
                actualCostTotal: totalCost,
                expectedCostTotal: totalCost,
                operationalCost: totalCost,
                totalCostIncludesBuyingRate: true,
                ...buildCostParts({
                    sourceType: "air",
                    row,
                    mblAttr,
                    shipmentId: s?.id || row._id || row.id,
                    shipmentCount: shipments.length,
                    masterNo: mblAttr?.amblNumber,
                }),
                _id: row._id || row.id || s.id || `${idx}`,
            };
        });

        const linehaulServiceProviderMap = {};

        shipments.forEach((s) => {
            const row = s?.attributes || s;
            const key = `AHFL${row.shipmentNo || ""}`;

            linehaulServiceProviderMap[key] =
                renderText(
                    row.serviceProvider ||
                    mblAttr?.airFreightDetails?.provider ||
                    mblAttr?.linehaulDetails?.provider,
                    "N/A"
                );
        });

        return {
            formattedShipments,
            linehaulServiceProviderMap,
        };
    };

    const handleGoInvoice = () => {
        if (!selectedMbl?.attributes) return;

        const payload = buildInvoicePayload(selectedMbl.attributes);

        if (!payload) {
            appAlert("This AMBL/DBL has no linked shipments. Invoice cannot be created.");
            return;
        }

        navigate("/create-invoice", {
            state: {
                shipments: payload.formattedShipments,
                linehaulServiceProviderMap: payload.linehaulServiceProviderMap,
                fromMbl: true,
                ambl: selectedMbl.attributes,
                sourceType: "air",
            },
        });
    };

    const mblTableData = useMemo(() => {
        return {
            columns: [
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>Type</span>
                    ),
                    accessor: "type",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              AMBL / DBL No
            </span>
                    ),
                    accessor: "amblNumber",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>COB</span>
                    ),
                    accessor: "cob",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>ETA</span>
                    ),
                    accessor: "eta",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>SB NO</span>
                    ),
                    accessor: "sealNumber",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>CMR</span>
                    ),
                    accessor: "cmrNumber",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Plate / Container
            </span>
                    ),
                    accessor: "plateContainerNumber",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Linked Shipments
            </span>
                    ),
                    accessor: "shipmentCount",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>Actions</span>
                    ),
                    accessor: "actions",
                },
            ],
            rows: mbls.map((mbl) => {
                const attr = mbl.attributes || {};
                const shipments = getMblShipments(attr);
                const shipmentCount = shipments.length;
                const isSelected = selectedMbl?.id === mbl.id;
                const typeLabel = getBlTypeLabel(attr);

                const rowStyle = {
                    background: isSelected ? "#eef6ff" : "transparent",
                    borderRadius: 8,
                    padding: "6px 8px",
                    cursor: "pointer",
                    fontWeight: isSelected ? 700 : 500,
                };

                return {
                    type: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            <MDBox
                                px={1.5}
                                py={0.5}
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "999px",
                                    background: typeLabel === "DBL" ? "#fff7ed" : "#eef6ff",
                                    color: typeLabel === "DBL" ? "#c2410c" : "#1565c0",
                                    fontWeight: 700,
                                    minWidth: 58,
                                }}
                            >
                                {typeLabel}
                            </MDBox>
                        </div>
                    ),

                    amblNumber: (
                        <div style={rowStyle}>
                            <ListLink onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditMbl(mbl);
                                }}>
                                {renderText(attr.amblNumber)}
                            </ListLink>
                        </div>
                    ),

                    cob: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            {formatDateTime(attr.cob, attr.cobTime)}
                        </div>
                    ),

                    eta: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            {formatDateTime(attr.eta, attr.etaTime)}
                        </div>
                    ),

                    sealNumber: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            {renderText(attr.sealNumber)}
                        </div>
                    ),

                    cmrNumber: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            {renderText(attr.cmrNumber)}
                        </div>
                    ),

                    plateContainerNumber: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            {renderText(attr.plateContainerNumber)}
                        </div>
                    ),

                    shipmentCount: (
                        <div style={rowStyle} onClick={() => setSelectedMbl(mbl)}>
                            <MDBox
                                px={1.5}
                                py={0.5}
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "999px",
                                    background: isSelected ? "#dbeafe" : "#eef6ff",
                                    color: "#1565c0",
                                    fontWeight: 700,
                                    minWidth: 36,
                                }}
                            >
                                {shipmentCount}
                            </MDBox>
                        </div>
                    ),

                    actions: (
                        <CrmRowActions onEdit={() => handleEditMbl(mbl)} editTitle={`Edit ${typeLabel}`} onDelete={() => setDeleteDialog({ open: true, mbl })} deleteTitle={`Delete ${typeLabel}`}><ListIconButton title="View Details" onClick={() => setSelectedMbl(mbl)}><VisibilityOutlinedIcon /></ListIconButton></CrmRowActions>
                    ),
                };
            }),
        };
    }, [mbls, selectedMbl]);

    const mblShipmentTableData = useMemo(() => {
        if (!selectedMbl?.attributes) return null;

        const attr = selectedMbl.attributes;
        const shipments = getMblShipments(attr);

        if (!Array.isArray(shipments) || shipments.length === 0) return null;

        const fmt = (v) => {
            const n = Number(v || 0);
            return Number.isFinite(n) ? n.toFixed(4) : "0.0000";
        };

        return {
            columns: [
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Shipment No
            </span>
                    ),
                    accessor: "shipmentNo",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Customer
            </span>
                    ),
                    accessor: "customer",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Total LDM
            </span>
                    ),
                    accessor: "totalLdm",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Total CBM
            </span>
                    ),
                    accessor: "totalCbm",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Gross Weight
            </span>
                    ),
                    accessor: "totalGrossWeight",
                },
                {
                    Header: () => (
                        <span style={{ fontWeight: "bold", fontSize: "14px" }}>
              Chargeable Weight
            </span>
                    ),
                    accessor: "totalChargeableWeight",
                },
            ],
            rows: shipments.map((s, idx) => {
                const row = s?.attributes || s;
                const shipmentId = s?.id || row?.id || row?._id || "";

                return {
                    shipmentNo: (
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="info"
                            sx={{
                                cursor: shipmentId ? "pointer" : "default",
                                textDecoration: shipmentId ? "underline" : "none",
                            }}
                            onClick={() => handleGoShipmentForm(shipmentId)}
                        >
                            {`AHFL${row.shipmentNo || idx + 1}`}
                        </MDTypography>
                    ),
                    customer: renderText(row.customer),
                    totalLdm: fmt(row.totals?.totalLdm),
                    totalCbm: fmt(row.totals?.totalCbm),
                    totalGrossWeight: fmt(row.totals?.totalGrossWeight),
                    totalChargeableWeight: fmt(row.totals?.totalChargeableWeight),
                };
            }),
        };
    }, [selectedMbl]);

    const selectedAttr = selectedMbl?.attributes || null;
    const selectedBlTypeLabel = getBlTypeLabel(selectedAttr || {});
    const selectedShipments = getMblShipments(selectedAttr || {});
    const selectedShipmentCount = selectedShipments.length;
    const hasShipmentsForInvoice = selectedShipmentCount > 0;

    const linehaulDetails =
        selectedAttr?.airFreightDetails ||
        selectedAttr?.linehaulDetails ||
        {};

    const freightCharges = selectedAttr?.freightCharges || {};
    const financialSummary = selectedAttr?.financialSummary || {};

    const summaryCurrency = renderText(
        financialSummary?.totalCurrency ||
        freightCharges?.totalCurrency ||
        linehaulDetails?.currency ||
        "EUR",
        "EUR"
    );

    const providerName = renderText(
        linehaulDetails?.provider ||
        selectedAttr?.selectedLinehaulProvider ||
        selectedAttr?.serviceProvider
    );

    const linehaulCost =
        linehaulDetails?.cost ??
        freightCharges?.airFreight?.cost ??
        0;

    const linehaulCurrency = renderText(
        linehaulDetails?.currency ||
        freightCharges?.airFreight?.currency ||
        summaryCurrency,
        summaryCurrency
    );

    const pickupTotal =
        financialSummary?.pickupTotal ??
        freightCharges?.pickupTotal ??
        0;

    const othcTotal =
        financialSummary?.othcTotal ??
        freightCharges?.othc?.total ??
        0;

    const airFreightTotal =
        financialSummary?.airFreightTotal ??
        freightCharges?.airFreight?.total ??
        linehaulCost ??
        0;

    const dthcTotal =
        financialSummary?.dthcTotal ??
        freightCharges?.dthc?.total ??
        0;

    const deliveryTotal =
        financialSummary?.deliveryTotal ??
        freightCharges?.deliveryTotal ??
        0;

    const buyingRate =
        financialSummary?.buyingRate ??
        freightCharges?.buyingRate ??
        financialSummary?.totalCost ??
        0;

    const totalSellingRate =
        financialSummary?.totalSellingRate ??
        financialSummary?.shipmentSellingTotal ??
        0;

    const totalProfit = financialSummary?.totalProfit ?? 0;

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox pt={3} pb={3} display="flex" flexDirection="column" gap={2.5}>
                {notification.value && (
<CrmNotice tone={notification.type === "success" ? "green" : "red"}>{notification.text}</CrmNotice>
)}

                <CrmPageHeader
 title="AMBL / DBL Management"
 subtitle="View, edit, review shipment links, and continue invoice operations from AMBL and Direct MBL records."
 actions={
<>
<CrmActionButton variant="secondary" icon={<RefreshIcon />} onClick={fetchMbls}>Refresh</CrmActionButton>
<CrmActionButton variant="secondary" icon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Back</CrmActionButton>
</>
}
/>

                <CrmTableCard title="AMBL / DBL list" summary={`${mbls.length} records · click a row or the view icon to see its details`}>
<DataTable table={mblTableData} canSearch />
</CrmTableCard>

                {selectedAttr ? (
                    <MDBox p={3} sx={cardSx}>
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={3}
                            flexWrap="wrap"
                            gap={2}
                        >
                            <MDBox>
                                <MDTypography variant="h5" fontWeight="bold">
                                    {selectedBlTypeLabel} Details
                                </MDTypography>

                                <MDTypography variant="button" color="text">
                                    {renderText(selectedAttr.amblNumber)}
                                </MDTypography>
                            </MDBox>

                            <MDBox display="flex" gap={1} flexWrap="wrap">
                                <CrmActionButton variant="secondary" icon={<EditIcon />} onClick={() => handleEditMbl(selectedMbl)}>Edit {selectedBlTypeLabel}</CrmActionButton>

                                <CrmActionButton icon={<DescriptionOutlinedIcon />} onClick={handleGoInvoice} disabled={!hasShipmentsForInvoice}>Invoice Details</CrmActionButton>
                            </MDBox>
                        </MDBox>

                        <MDBox display="flex" flexWrap="wrap" gap={2} mb={3}>
                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Type</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {selectedBlTypeLabel}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">COB</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatDateTime(selectedAttr.cob, selectedAttr.cobTime)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">ETA</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatDateTime(selectedAttr.eta, selectedAttr.etaTime)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Cut-off</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatDateTime(selectedAttr.cutOffDate, selectedAttr.cutOffTime)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Free Time</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {selectedAttr.freeTime || selectedAttr.freeTime === 0
                                        ? `${renderText(selectedAttr.freeTime)} hours`
                                        : "-"}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Flight Name</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {renderText(selectedAttr.vesselName)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Transit Time</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {selectedAttr.transitTime || selectedAttr.transitTime === 0
                                        ? `${renderText(selectedAttr.transitTime)} days`
                                        : "-"}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">SB Number</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {renderText(selectedAttr.sealNumber)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">CMR Number</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {renderText(selectedAttr.cmrNumber)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Plate / Container</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {renderText(selectedAttr.plateContainerNumber)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Linked Shipments</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {selectedShipmentCount}
                                </MDTypography>
                            </MDBox>
                        </MDBox>

                        <MDTypography variant="h6" fontWeight="bold" mb={2}>
                            Freight & Financial Summary
                        </MDTypography>

                        <MDBox display="flex" flexWrap="wrap" gap={2} mb={3}>
                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">
                                    Air Freight Provider
                                </MDTypography>

                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {providerName}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">
                                    Air Freight Cost
                                </MDTypography>

                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(linehaulCost, linehaulCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">P/U Cost</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(pickupTotal, summaryCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">OTHC</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(othcTotal, summaryCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">
                                    Air Freight Total
                                </MDTypography>

                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(airFreightTotal, summaryCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">DTHC</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(dthcTotal, summaryCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Delivery Cost</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(deliveryTotal, summaryCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">Buying Rate</MDTypography>
                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(buyingRate, summaryCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox sx={infoCardSx}>
                                <MDTypography variant="caption" color="text">
                                    Total Selling Rate
                                </MDTypography>

                                <MDTypography variant="button" fontWeight="bold" display="block" mt={0.5}>
                                    {formatMoney(totalSellingRate, summaryCurrency)}
                                </MDTypography>
                            </MDBox>

                            <MDBox
                                sx={{
                                    ...infoCardSx,
                                    background: Number(totalProfit || 0) >= 0 ? "#edf9f0" : "#fff1f2",
                                    border:
                                        Number(totalProfit || 0) >= 0
                                            ? "1px solid #b7ebc6"
                                            : "1px solid #f5c2c7",
                                }}
                            >
                                <MDTypography variant="caption" color="text">
                                    Total Profit
                                </MDTypography>

                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    display="block"
                                    mt={0.5}
                                    color={Number(totalProfit || 0) >= 0 ? "success" : "error"}
                                >
                                    {formatMoney(totalProfit, summaryCurrency)}
                                </MDTypography>
                            </MDBox>
                        </MDBox>

                        {mblShipmentTableData ? (
                            <MDBox>
                                <MDTypography variant="h6" fontWeight="bold" mb={1}>
                                    Linked Shipments
                                </MDTypography>

                                <MDTypography variant="caption" color="text" display="block" mb={2}>
                                    All shipment records connected to the selected {selectedBlTypeLabel} are shown below.
                                </MDTypography>

                                <DataTable table={mblShipmentTableData} canSearch />
                            </MDBox>
                        ) : (
                            <MDBox
                                p={3}
                                sx={{
                                    border: "1px dashed #d0d7de",
                                    borderRadius: "12px",
                                    background: "#fafbfc",
                                    textAlign: "center",
                                }}
                            >
                                <MDTypography variant="button" fontWeight="medium">
                                    No linked shipment found for this {selectedBlTypeLabel}.
                                </MDTypography>
                            </MDBox>
                        )}
                    </MDBox>
                ) : (
                    <MDBox
                        p={4}
                        sx={{
                            ...cardSx,
                            textAlign: "center",
                            background: "#fcfcfd",
                        }}
                    >
                        <ZoomInIcon sx={{ fontSize: 42, color: "#90a4ae", mb: 1 }} />

                        <MDTypography variant="h6" fontWeight="bold" mb={1}>
                            Select an AMBL / DBL record
                        </MDTypography>

                        <MDTypography variant="button" color="text">
                            Click any record row or use the view icon to inspect details and linked shipments.
                        </MDTypography>
                    </MDBox>
                )}
            </MDBox>

            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, mbl: null })}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    Delete {getBlTypeLabel(deleteDialog?.mbl?.attributes || {})}
                </DialogTitle>

                <DialogContent>
                    <MDTypography variant="button">
                        Are you sure you want to delete{" "}
                        <b>{renderText(deleteDialog?.mbl?.attributes?.amblNumber)}</b>?
                    </MDTypography>
                </DialogContent>

                <DialogActions>
                    <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={() => setDeleteDialog({ open: false, mbl: null })}
                    >
                        Cancel
                    </MDButton>

                    <MDButton variant="contained" color="error" onClick={clickDeleteMbl}>
                        Delete
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default AirConsolidation;